import {
    useEffect,
    useState,
    useMemo,
    useRef,
    type FC,
    useId
} from "react";
import {
    type PanResponderGestureState,
    type LayoutChangeEvent,
    PanResponder,
    View
} from "react-native";
import type {
    SliderThumbKeyboardEventType
} from "./components/thumb/type";
import type ISliderProps from "./type";
import type {
    SliderGestureHandlersType,
    SliderThumbIndex,
    SliderRangeValue,
    SliderDragType,
    SliderLabel,
    SliderValue,
    SliderTick
} from "./type";
import stylesheet, {
    getSliderIconStyle,
    getSliderMeasures,
    getSliderColors,
    useStyles
} from "./stylesheet";
import SliderScale from "./components/scale";
import SliderThumb from "./components/thumb";
import {
    getSliderOffsetFraction,
    getSliderPointerOffset,
    getIsSliderTipOverlap,
    getSliderValueText,
    getSliderKeyAction,
    getSliderFraction,
    clampSliderValue,
    snapSliderValue,
    moveSliderValue,
    getSliderValues,
    getSliderOrigin,
    getSliderLabels,
    getSliderStops,
    getSliderTicks,
    getSliderUnit
} from "./util";
import {
    NCoreUIKitLocalize,
    NCoreUIKitTheme
} from "../../core/hooks";
import {
    measurePointerRect,
    usePointerType
} from "../../helpers/pointer";
import {
    isolateText
} from "../../helpers/locale";
import {
    warnOnce
} from "../../utils/development";
import Text from "../text";

const Slider: FC<ISliderProps> = ({
    rightIcon: RightIconComponent,
    tooltipBehaviour = "dragging",
    isShowMinMaxLabels = false,
    orientation = "horizontal",
    minStepsBetweenThumbs = 0,
    "aria-label": ariaLabel,
    icon: IconComponent,
    isShowTicks = false,
    accessibilityLabel,
    isDisabled = false,
    max: maxProp = 100,
    type = "primary",
    value: valueProp,
    formatValueText,
    size = "medium",
    customLocalize,
    step: stepProp,
    onValueChange,
    defaultValue,
    initialValue,
    customTheme,
    isShowValue,
    onChangeEnd,
    thumbTitles,
    hintText,
    onChange,
    min = 0,
    marks,
    style,
    title,
    ...props
}) => {
    const {
        controlPaddings,
        controlHeights,
        shapeConfigs,
        touchTargets,
        typography,
        durations,
        iconSizes,
        radiuses,
        borders,
        colors,
        spaces
    } = NCoreUIKitTheme.useContext(customTheme);

    const {
        activeLocale,
        direction,
        localize
    } = NCoreUIKitLocalize.useContext(customLocalize);

    const pointerType = usePointerType();

    const idPrefix = useId().replace(/[^a-zA-Z0-9]/g, "");

    const titleID = `${idPrefix}-title`;

    const hintID = `${idPrefix}-hint`;

    const [
        innerValue,
        setInnerValue
    ] = useState<SliderValue>(defaultValue ?? initialValue ?? min);

    const [
        keyboardIndex,
        setKeyboardIndex
    ] = useState<SliderThumbIndex | null>(null);

    const [
        focusIndex,
        setFocusIndex
    ] = useState<SliderThumbIndex | null>(null);

    const [
        dragIndex,
        setDragIndex
    ] = useState<SliderThumbIndex | null>(null);

    const [
        length,
        setLength
    ] = useState<number>(0);

    const railRef = useRef<View>(null);

    const fromThumbRef = useRef<View>(null);

    const toThumbRef = useRef<View>(null);

    const keyboardTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    const dragRef = useRef<SliderDragType>({
        isChanged: false,
        isPending: false,
        isActive: false,
        origin: null,
        index: null,
        extent: 0
    });

    const max = Math.max(maxProp, min);

    const step = stepProp !== undefined && stepProp > 0 ? stepProp : 1;

    const stops = getSliderStops({
        step: stepProp,
        marks
    });

    const gap = minStepsBetweenThumbs * getSliderUnit({
        stops,
        step
    });

    const sourceValue = valueProp ?? innerValue;

    const isRange = Array.isArray(sourceValue);

    const values = getSliderValues({
        value: sourceValue,
        min,
        max
    });

    const valuesRef = useRef<SliderRangeValue>(values);

    valuesRef.current = values;

    const currentMeasures = getSliderMeasures({
        controlPaddings,
        controlHeights,
        shapeConfigs,
        touchTargets,
        pointerType,
        iconSizes,
        borders,
        size
    });

    const currentColors = getSliderColors({
        isDisabled,
        colors,
        type
    });

    const usable = Math.max(length - currentMeasures.thumbSize, 0);

    const fractions: SliderRangeValue = [
        getSliderFraction({
            value: values[0],
            min,
            max
        }),
        getSliderFraction({
            value: values[1],
            min,
            max
        })
    ];

    const rangeStart = isRange ? usable * fractions[0] + currentMeasures.thumbSize / 2 : 0;

    const rangeSize = isRange ? usable * (fractions[1] - fractions[0]) : usable * fractions[1] + currentMeasures.thumbSize / 2;

    const {
        container: containerDynamicStyle,
        range: rangeDynamicStyle,
        track: trackDynamicStyle,
        body: bodyDynamicStyle,
        head: headDynamicStyle,
        icon: iconDynamicStyle,
        rail: railDynamicStyle
    } = useStyles({
        controlPaddings,
        currentMeasures,
        controlHeights,
        currentColors,
        shapeConfigs,
        orientation,
        rangeStart,
        rangeSize,
        radiuses,
        spaces
    });

    const formatText = (value: number) => getSliderValueText({
        locale: activeLocale,
        formatValueText,
        marks,
        value
    });

    const valueTexts: [string, string] = [
        formatText(values[0]),
        formatText(values[1])
    ];

    const toSliderValue = (nextValues: SliderRangeValue): SliderValue => {
        if(isRange) {
            return nextValues;
        }

        return nextValues[1];
    };

    const handleCommit = (nextValues: SliderRangeValue) => {
        valuesRef.current = nextValues;

        const nextValue = toSliderValue(nextValues);

        if(valueProp === undefined) {
            setInnerValue(nextValue);
        }

        onValueChange?.(nextValue);

        onChange?.(nextValue);
    };

    const handleThumbValue = (index: SliderThumbIndex, nextValue: number): boolean => {
        const currentValues = valuesRef.current;

        const lower = index === 1 && isRange ? currentValues[0] + gap : min;

        const upper = index === 0 ? currentValues[1] - gap : max;

        if(lower > upper) {
            return false;
        }

        const boundedValue = clampSliderValue({
            value: nextValue,
            min: lower,
            max: upper
        });

        if(boundedValue === currentValues[index]) {
            return false;
        }

        handleCommit(index === 0 ? [
            boundedValue,
            currentValues[1]
        ] : [
            currentValues[0],
            boundedValue
        ]);

        return true;
    };

    const handleClearKeyboardTimer = () => {
        if(keyboardTimerRef.current === null) {
            return;
        }

        clearTimeout(keyboardTimerRef.current);

        keyboardTimerRef.current = null;
    };

    const getThumbRef = (index: SliderThumbIndex) => {
        return index === 0 ? fromThumbRef : toThumbRef;
    };

    const handleFocusThumb = (index: SliderThumbIndex) => {
        getThumbRef(index).current?.focus?.();
    };

    const getPointerValue = (offset: number, extent: number): number => {
        const fraction = getSliderOffsetFraction({
            thumbSize: currentMeasures.thumbSize,
            length: extent,
            offset
        });

        return snapSliderValue({
            value: min + fraction * (max - min),
            stops,
            step,
            min,
            max
        });
    };

    const getThumbCenter = (value: number, extent: number): number => {
        return currentMeasures.thumbSize / 2 + Math.max(extent - currentMeasures.thumbSize, 0) * getSliderFraction({
            value,
            min,
            max
        });
    };

    const handleStartDrag = (index: SliderThumbIndex) => {
        dragRef.current.index = index;

        dragRef.current.isPending = false;

        setDragIndex(index);

        handleFocusThumb(index);
    };

    const handleGrant = ({
        x0,
        y0
    }: PanResponderGestureState) => {
        if(isDisabled) {
            return;
        }

        dragRef.current = {
            isChanged: false,
            isPending: false,
            isActive: true,
            origin: null,
            index: null,
            extent: 0
        };

        measurePointerRect(railRef.current, ({
            height,
            pageX,
            width,
            pageY
        }) => {
            const extent = orientation === "vertical" ? height : width;

            const origin = getSliderOrigin({
                orientation,
                direction,
                height,
                pageX,
                pageY,
                width
            });

            const offset = getSliderPointerOffset({
                pointer: orientation === "vertical" ? y0 : x0,
                orientation,
                direction,
                origin
            });

            const currentValues = valuesRef.current;

            dragRef.current.origin = origin;

            dragRef.current.extent = extent;

            let index: SliderThumbIndex = 1;

            if(isRange) {
                const firstDistance = Math.abs(getThumbCenter(currentValues[0], extent) - offset);

                const secondDistance = Math.abs(getThumbCenter(currentValues[1], extent) - offset);

                const firstCenter = getThumbCenter(currentValues[0], extent);

                if(firstDistance === secondDistance && (firstCenter !== getThumbCenter(currentValues[1], extent) || offset === firstCenter)) {
                    dragRef.current.isPending = true;

                    return;
                }

                if(firstDistance === secondDistance) {
                    index = offset > firstCenter ? 1 : 0;
                } else {
                    index = firstDistance < secondDistance ? 0 : 1;
                }
            }

            if(dragRef.current.isActive) {
                handleStartDrag(index);
            }

            if(handleThumbValue(index, getPointerValue(offset, extent))) {
                dragRef.current.isChanged = true;
            }

            if(!dragRef.current.isActive) {
                onChangeEnd?.(toSliderValue(valuesRef.current));
            }
        });
    };

    const handleMove = ({
        moveX,
        moveY
    }: PanResponderGestureState) => {
        const {
            isPending,
            extent,
            origin
        } = dragRef.current;

        if(isDisabled || origin === null) {
            return;
        }

        const offset = getSliderPointerOffset({
            pointer: orientation === "vertical" ? moveY : moveX,
            orientation,
            direction,
            origin
        });

        let index = dragRef.current.index;

        if(isPending) {
            const center = getThumbCenter(valuesRef.current[0], extent);

            if(offset === center) {
                return;
            }

            index = offset > center ? 1 : 0;

            handleStartDrag(index);
        }

        if(index === null) {
            return;
        }

        if(handleThumbValue(index, getPointerValue(offset, extent))) {
            dragRef.current.isChanged = true;
        }
    };

    const handleRelease = () => {
        const wasActive = dragRef.current.isActive;

        const wasChanged = dragRef.current.isChanged;

        dragRef.current.isActive = false;

        dragRef.current.isPending = false;

        dragRef.current.index = null;

        setDragIndex(null);

        if(wasActive && (wasChanged || dragRef.current.origin !== null)) {
            onChangeEnd?.(toSliderValue(valuesRef.current));
        }
    };

    const handleThumbKeyDown = (index: SliderThumbIndex, event: SliderThumbKeyboardEventType) => {
        if(isDisabled) {
            return;
        }

        const action = getSliderKeyAction({
            shiftKey: event.shiftKey,
            key: event.key,
            direction
        });

        if(action === null) {
            return;
        }

        event.preventDefault();

        handleClearKeyboardTimer();

        setKeyboardIndex(index);

        let nextValue = valuesRef.current[index];

        if(action === "min") {
            nextValue = min;
        } else if(action === "max") {
            nextValue = max;
        } else {
            nextValue = moveSliderValue({
                value: valuesRef.current[index],
                count: action,
                stops,
                step,
                min,
                max
            });
        }

        if(handleThumbValue(index, nextValue)) {
            dragRef.current.isChanged = true;
        }
    };

    const handleThumbKeyUp = () => {
        if(!dragRef.current.isChanged || dragRef.current.isActive) {
            return;
        }

        dragRef.current.isChanged = false;

        onChangeEnd?.(toSliderValue(valuesRef.current));

        handleClearKeyboardTimer();

        keyboardTimerRef.current = setTimeout(() => {
            setKeyboardIndex(null);

            keyboardTimerRef.current = null;
        }, durations.long);
    };

    const handleThumbFocus = (index: SliderThumbIndex) => {
        setFocusIndex(index);
    };

    const handleThumbBlur = () => {
        handleClearKeyboardTimer();

        setFocusIndex(null);

        setKeyboardIndex(null);
    };

    const handleThumbStep = (index: SliderThumbIndex, count: 1 | -1) => {
        const changed = handleThumbValue(index, moveSliderValue({
            value: valuesRef.current[index],
            count,
            stops,
            step,
            min,
            max
        }));

        if(changed) {
            onChangeEnd?.(toSliderValue(valuesRef.current));
        }
    };

    const latestRef = useRef<SliderGestureHandlersType>({
        handleRelease,
        handleGrant,
        isDisabled,
        handleMove
    });

    latestRef.current = {
        handleRelease,
        handleGrant,
        isDisabled,
        handleMove
    };

    const panResponder = useMemo(() => PanResponder.create({
        onPanResponderGrant: (_event, gestureState) => latestRef.current.handleGrant(gestureState),
        onPanResponderMove: (_event, gestureState) => latestRef.current.handleMove(gestureState),
        onStartShouldSetPanResponder: () => !latestRef.current.isDisabled,
        onMoveShouldSetPanResponder: () => !latestRef.current.isDisabled,
        onPanResponderTerminate: () => latestRef.current.handleRelease(),
        onPanResponderRelease: () => latestRef.current.handleRelease(),
        onPanResponderTerminationRequest: () => false
    }), []);

    useEffect(() => {
        return () => {
            if(keyboardTimerRef.current !== null) {
                clearTimeout(keyboardTimerRef.current);
            }
        };
    }, []);

    useEffect(() => {
        if(initialValue === undefined) {
            return;
        }

        warnOnce({
            message: "Slider: \"initialValue\" is deprecated and will be removed in the next major version. Use \"defaultValue\" instead."
        });
    }, [
        initialValue
    ]);

    useEffect(() => {
        if(onChange === undefined) {
            return;
        }

        warnOnce({
            message: "Slider: \"onChange\" is deprecated and will be removed in the next major version. Use \"onValueChange\" instead."
        });
    }, [
        onChange
    ]);

    useEffect(() => {
        if(title || ariaLabel || accessibilityLabel || thumbTitles) {
            return;
        }

        warnOnce({
            message: "Slider: a slider needs a \"title\", \"thumbTitles\", \"aria-label\" or \"accessibilityLabel\" so screen readers can announce it."
        });
    }, [
        accessibilityLabel,
        thumbTitles,
        ariaLabel,
        title
    ]);

    const onRailLayout = ({
        nativeEvent
    }: LayoutChangeEvent) => {
        setLength(orientation === "vertical" ? nativeEvent.layout.height : nativeEvent.layout.width);
    };

    const tipDistance = isRange ? usable * (fractions[1] - fractions[0]) : 0;

    const isTipMerged = isRange && tooltipBehaviour === "always" && getIsSliderTipOverlap({
        labelVariant: currentMeasures.labelVariant,
        padding: spaces.spacingSm,
        distance: tipDistance,
        texts: valueTexts,
        typography
    });

    const getTipText = (index: SliderThumbIndex) => {
        if(isTipMerged) {
            return index === 1 ? `${valueTexts[0]} – ${valueTexts[1]}` : undefined;
        }

        const isShow = tooltipBehaviour === "always" || tooltipBehaviour === "dragging" && (dragIndex === index || keyboardIndex === index || focusIndex === index);

        return isShow ? valueTexts[index] : undefined;
    };

    const thumbIndexes: Array<SliderThumbIndex> = isRange ? [
        0,
        1
    ] : [
        1
    ];

    const isValueShown = isShowValue ?? Boolean(title);

    const labels: Array<SliderLabel> = orientation === "horizontal" ? getSliderLabels({
        isShowMinMaxLabels,
        formatText,
        marks,
        min,
        max
    }) : [];

    const ticks: Array<SliderTick> = orientation === "horizontal" && isShowTicks ? getSliderTicks({
        minSpacing: spaces.spacingSm,
        usable,
        stops,
        step,
        min,
        max
    }).map((value) => ({
        isActive: value <= values[1] && value >= values[0],
        fraction: getSliderFraction({
            value,
            min,
            max
        })
    })) : [];

    const getThumbLabelProps = (index: SliderThumbIndex) => {
        if(!isRange) {
            return title ? {
                "aria-labelledby": titleID
            } : {
                "aria-label": ariaLabel ?? accessibilityLabel
            };
        }

        const thumbLabel = localize(index === 0 ? "slider-minimum-thumb" : "slider-maximum-thumb");

        return {
            "aria-label": thumbTitles?.[index] ?? (title ? `${title}, ${thumbLabel}` : thumbLabel)
        };
    };

    const renderHead = () => {
        if(!title && !isValueShown) {
            return null;
        }

        const valueText = isRange ? `${isolateText({
            text: valueTexts[0]
        })} – ${isolateText({
            text: valueTexts[1]
        })}` : valueTexts[1];

        return <View
            style={[
                stylesheet.head,
                orientation === "vertical" ? stylesheet.verticalHead : null,
                headDynamicStyle
            ]}
        >
            {
                title ?
                    <Text
                        customColor={currentColors.labelColor}
                        variant={currentMeasures.labelVariant}
                        customTheme={customTheme}
                        style={stylesheet.label}
                        numberOfLines={1}
                        id={titleID}
                    >
                        {title}
                    </Text>
                :
                    null
            }
            {
                isValueShown ?
                    <Text
                        customColor={currentColors.valueColor}
                        variant={currentMeasures.labelVariant}
                        customTheme={customTheme}
                        style={stylesheet.value}
                    >
                        {valueText}
                    </Text>
                :
                    null
            }
        </View>;
    };

    const renderIcon = (Component: ISliderProps["icon"], isEnd: boolean) => {
        if(!Component) {
            return null;
        }

        return <View
            importantForAccessibility="no-hide-descendants"
            style={[
                stylesheet.icon,
                iconDynamicStyle,
                getSliderIconStyle({
                    controlPaddings,
                    orientation,
                    isEnd
                })
            ]}
            aria-hidden={true}
        >
            <Component
                customColor={currentColors.iconColor}
                size={currentMeasures.iconSize}
                color="mid"
            />
        </View>;
    };

    const renderThumb = (index: SliderThumbIndex) => {
        return <SliderThumb
            {...getThumbLabelProps(index)}
            valueMin={index === 1 && isRange ? values[0] + gap : min}
            aria-describedby={hintText ? hintID : undefined}
            tipDistance={isTipMerged ? tipDistance / 2 : 0}
            valueMax={index === 0 ? values[1] - gap : max}
            offset={usable * fractions[index]}
            currentMeasures={currentMeasures}
            isPressed={dragIndex === index}
            onKeyDown={handleThumbKeyDown}
            valueText={valueTexts[index]}
            currentColors={currentColors}
            viewRef={getThumbRef(index)}
            tipText={getTipText(index)}
            onKeyUp={handleThumbKeyUp}
            onFocus={handleThumbFocus}
            customTheme={customTheme}
            orientation={orientation}
            pointerType={pointerType}
            onBlur={handleThumbBlur}
            onStep={handleThumbStep}
            valueNow={values[index]}
            isDisabled={isDisabled}
            index={index}
            key={index}
        />;
    };

    const renderScale = () => {
        if(!ticks.length && !labels.length) {
            return null;
        }

        return <SliderScale
            currentMeasures={currentMeasures}
            currentColors={currentColors}
            customTheme={customTheme}
            length={length}
            labels={labels}
            ticks={ticks}
        />;
    };

    const renderHint = () => {
        if(!hintText) {
            return null;
        }

        return <Text
            customColor={currentColors.hintColor}
            customTheme={customTheme}
            variant="bodySmallSize"
            id={hintID}
        >
            {hintText}
        </Text>;
    };

    return <View
        {...props}
        style={[
            style,
            stylesheet.container,
            orientation === "vertical" ? stylesheet.verticalContainer : null,
            containerDynamicStyle
        ]}
    >
        {renderHead()}
        <View
            style={[
                stylesheet.body,
                orientation === "vertical" ? stylesheet.verticalBody : null,
                bodyDynamicStyle
            ]}
        >
            {renderIcon(IconComponent, false)}
            <View
                style={stylesheet.main}
            >
                <View
                    {...panResponder.panHandlers}
                    style={[
                        stylesheet.rail,
                        orientation === "vertical" ? stylesheet.verticalRail : stylesheet.horizontalRail,
                        railDynamicStyle
                    ]}
                    onLayout={onRailLayout}
                    ref={railRef}
                >
                    <View
                        style={[
                            stylesheet.track,
                            orientation === "vertical" ? stylesheet.verticalTrack : stylesheet.horizontalTrack,
                            trackDynamicStyle
                        ]}
                    >
                        <View
                            style={[
                                stylesheet.range,
                                orientation === "vertical" ? stylesheet.verticalRange : stylesheet.horizontalRange,
                                rangeDynamicStyle
                            ]}
                        />
                    </View>
                    {thumbIndexes.map((index) => renderThumb(index))}
                </View>
                {renderScale()}
            </View>
            {renderIcon(RightIconComponent, true)}
        </View>
        {renderHint()}
    </View>;
};
export default Slider;
